
// Libraries
import { Component, namespace, Vue, Watch } from "nuxt-property-decorator";

// Components
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import ExpandablePriceInfo from "~/components/molecules/display/ExpandablePriceInfo.vue";
import PelletsPriceDisplay from "~/components/molecules/display/PelletsPriceDisplay.vue";
import PelletsChartComparison from "~/components/organisms/chart/PelletsChartComparison.vue";
import PriceTrendSection from "~/components/organisms/display/geographySites/PriceTrendSection.vue";
import PlaceListSection from "~/components/organisms/display/PlaceListSection.vue";
import PriceComparisonH1 from "~/components/organisms/display/PriceComparisonH1.vue";

// Helpers
import { createBreadCrumbsStructuredData } from "~/mixins/breadcrumb";
import { replaceToUmlauts, returnPriceFormatDE } from "~/mixins/formatting";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { decodePlaceName } from "~/operations/shared";
import { STANDARD_PELLETS_QUANTITY } from "~/types";
import { CountyLink } from "~/types/Location";
import { getMaxBruttoPrice, getMinBruttoPrice } from "~/utils/priceUtils";

const FederalStatesModule = namespace("federalstates");
const BreadCrumbModule = namespace("breadcrumb");
const OilpriceModule = namespace("oilprice");
const CalculatorModule = namespace("calculator");

@Component({
  scrollToTop: true,
  layout: "shop",
  components: {
    BreadCrumb,
    PriceComparisonH1,
    PlaceListSection,
    PelletsPriceDisplay,
    ExpandablePriceInfo,
    PriceTrendSection,
    PelletsChartComparison,
  },
})
export default class PelletsInfoPage extends Vue {
  @FederalStatesModule.Action("fetchCityName")
  fetchCityName;

  @FederalStatesModule.State("cityName")
  cityName;

  @FederalStatesModule.State("pelletsPriceHistory")
  pelletsPriceHistory;

  @FederalStatesModule.Action("fetchCountyIdentData")
  fetchCountyIdentData: any;

  @FederalStatesModule.Action("fetchPelletsHistoricalPriceData")
  fetchPelletsHistoricalPriceData;

  @CalculatorModule.State("dealerOffersData")
  dealerOffersData: any;

  @FederalStatesModule.State("cityRequestData")
  cityRequestData: any;

  @OilpriceModule.State("cityPlaces")
  cityPlaces: any;

  @OilpriceModule.Action("fetchCityPlacesData")
  fetchCityPlacesData: any;

  @FederalStatesModule.State("city")
  city: any;

  @FederalStatesModule.Action("fetchOtherZipcodes")
  fetchOtherZipcodes: any;

  @FederalStatesModule.Action("fetchCommunityLinks")
  fetchCommunityLinks: any;

  @FederalStatesModule.State("otherZipcodes")
  _otherZipcodes: any;

  @BreadCrumbModule.State("breadCrumb")
  breadCrumb;

  zipcode = "";

  initialPelletData = {
    loosePrice: 0,
    palletPrice: 0,
    looseLastWeekComparison: {
      percentage: "0",
      text: "",
    },
    palletLastWeekComparison: {
      percentage: "0",
      text: "",
    },
    looseNationalComparison: {
      percentage: "0",
      price: 0,
      text: "",
    },
    palletNationalComparison: {
      percentage: "0",
      price: 0,
      text: "",
    },
  };

  MAX_ITEMS_LIMIT = 50;

  STANDARD_PELLETS_QUANTITY = STANDARD_PELLETS_QUANTITY;

  head() {
    return {
      ...generateMetaTags(this.$route.path, "state-pages-pellets", [
        {
          k: "state",
          v: this.city.name,
        },
        {
          k: "zipcode",
          v: this.zipcode,
        },
      ]),
      link: [
        {
          rel: "canonical",
          href: `/pelletspreise/${this.$route.params.id.toLowerCase()}`,
        },
      ],
      script: [
        {
          json: {
            "@context": "http://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: createBreadCrumbsStructuredData(
              this.breadCrumb,
              "pellets",
            ),
          },
          type: "application/ld+json",
        },
        {
          json: {
            "@context": "http://schema.org",
            "@type": "Product",
            name: `Pellets ${this.city.zipcode} ${this.city.name}`,
            image: "https://cf.esyoil.com/img/esyoil/esyoil-social-image.png",
            brand: {
              "@type": "Brand",
              name: "Holzpellets EN plus A1 | Holzpellets DIN plus",
            },
            description: `Holzpellets EN plus A1 | Holzpellets DIN plus pro 1000 kg bei einer Abnahmemenge von ${STANDARD_PELLETS_QUANTITY} kg für einen Haushalt in ${this.city.zipcode} ${this.city.name}.`,
            url: `${process.env.MAIN_ESYOIL}${this.$route.fullPath} `,
            category: `Pellets ${this.city.zipcode} ${this.city.name}`,
            offers: {
              "@type": "AggregateOffer",
              lowPrice: this.getOffersMinPrice(),
              highPrice: this.getOffersMaxPrice(),
              priceCurrency: "EUR",
              offerCount: this.dealerOffersData.dealerOffers.length,
              offers: this.dealerOffersData.dealerOffers.map((offer) => {
                return {
                  "@type": "Offer",
                  price: offer.pricing.ton.brutto,
                  priceCurrency: "EUR",
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    price: offer.pricing.ton.brutto,
                    priceCurrency: "EUR",
                    unitCode: "KGS",
                    unitText: "1000 kg",
                  },
                  itemCondition: "https://schema.org/NewCondition",
                  availability: "https://schema.org/InStock",
                  shippingDetails: {
                    "@type": "OfferShippingDetails",
                    shippingRate: {
                      "@type": "MonetaryAmount",
                      currency: "EUR",
                      value: 0,
                    },
                    shippingDestination: {
                      "@type": "DefinedRegion",
                      addressCountry: "DE",
                      postalCode: this.city.zipcode,
                    },
                  },
                  seller: {
                    "@type": "Organization",
                    name: `${offer.dealer.name}, ${this.city.name}`,
                    legalName: offer.dealer.shortName,
                    url: `${process.env.MAIN_ESYOIL}/heizoelhaendler/${offer.dealer.slug}`,
                    logo: `https://cf.esyoil.${
                      process.env.MAIN_ESYOIL.includes("io") ? "io" : "com"
                    }${offer.dealer.logoPath}`,
                    email: offer.dealer.orderEmail,
                    address: {
                      "@type": "PostalAddress",
                      addressLocality: offer.dealer.address.city,
                      postalCode: offer.dealer.address.postalCode,
                      streetAddress: `${offer.dealer.address.street} ${offer.dealer.address.streetNumber}`,
                    },
                  },
                };
              }),
            },
          },
          type: "application/ld+json",
        },
      ],
    };
  }

  async fetch() {
    this.$store.commit("checkout/resetCheckoutStorage");
    await this.fetchLocationData();
    await Promise.all([
      this.fetchPelletHistoricalData(this.zipcode, 3),
      this.fetchOtherZipcodes({
        location: this.city.name,
        zipcode: this.zipcode,
        partnerType: "PARTNER_PELLET",
      }),
      this.fetchPelletsPriceTrend(),
    ]);

    this.storeInitialPelletData();

    this.validateZipcode();
  }

  async mounted() {
    await this.fetchCityPlaces();
  }

  async fetchLocationData(): Promise<void> {
    if (this.isNewUrlStructure) {
      await this.preflightLocationData();
    }
    await this.handleCountyLinks();
  }

  async fetchPelletsPriceTrend(): Promise<void> {
    await this.$store.dispatch("oilprice/fetchPelletsPriceTrend");
  }

  async fetchCounties(communityNutsCode: string): Promise<void> {
    await this.fetchCommunityLinks({
      communityNutsCode,
      partnerType: "PARTNER_PELLET",
    });
  }

  async fetchPelletHistoricalData(
    zipcode: string,
    months: number,
  ): Promise<void> {
    await this.fetchPelletsHistoricalPriceData({
      zipcode,
      months,
    });
  }

  async fetchCityPlaces(paramId?: string): Promise<void> {
    const cityName = paramId || this.city.name || this.$route.params.id;
    await this.fetchCityPlacesData({
      cityName,
      zipcode: this.city.zipcode,
      partnerType: "PARTNER_PELLET",
    });
  }

  async handleCountyLinks(): Promise<void> {
    const stateName = this.breadCrumb[1]?.name;
    const countyName = this.breadCrumb[2]?.name;
    if (countyName.startsWith("Kreisfreie Stadt")) {
      return;
    }

    if (
      stateName &&
      countyName &&
      !["hamburg", "bremen", "berlin"].includes(this.locationFromUrl)
    ) {
      await this.fetchCountytData(stateName, countyName);
      await this.fetchCounties(this.$store.state.federalstates.countyNutsCode);
    }
  }

  async fetchCountytData(stateName: string, countyName: string): Promise<void> {
    await this.fetchCountyIdentData({
      stateName,
      countyName,
      partnerType: "PARTNER_PELLET",
    });
  }

  async preflightLocationData(): Promise<void> {
    if (this.$route.query?.zipcode) {
      this.zipcode = this.$route.query?.zipcode as string;
    } else {
      this.zipcode = this.$route.params.id.split("-").pop();
    }

    const location = this.locationFromUrl;

    await this.fetchCityName({
      zipcode: this.zipcode,
      location: replaceToUmlauts(location),
      basePath: "pelletspreise",
    });
  }

  async updateRouteOnCityChange(zipcode): Promise<void> {
    await this.$router.replace({
      path: `/pelletspreise/pelletspreise-${decodePlaceName(
        this.city.name,
      )}-${zipcode}`,
      query: this.$route.query,
    });
  }

  getOffersMinPrice(): number {
    return getMinBruttoPrice(this.dealerOffersData.dealerOffers, "pellets");
  }

  getOffersMaxPrice(): number {
    return getMaxBruttoPrice(this.dealerOffersData.dealerOffers, "pellets");
  }

  getLastWeekPriceComparison(
    type: "loose" | "pallet",
    returnType: "percentage" | "text",
  ): string {
    const data = this.pelletsPriceHistory?.[type]?.data || [];
    const length = data.length;
    if (length < 8) return returnType === "percentage" ? "0" : "günstiger";

    const lastWeekPrice = data[length - 1 - 7]?.y; // price 7 days ago
    const actualPrice = this.getActualPrice(type);

    if (lastWeekPrice === 0 || actualPrice === 0) return "0";

    if (returnType === "percentage") {
      return String(
        this.calcPercentageValue(lastWeekPrice, actualPrice),
      ).replace(".", ",");
    } else {
      return lastWeekPrice <= actualPrice ? "günstiger" : "teurer";
    }
  }

  storeInitialPelletData(): void {
    this.initialPelletData = {
      loosePrice: this.getActualLoosePrice(),
      palletPrice: this.getActualPalletPrice(),
      looseLastWeekComparison: {
        percentage: this.getLastWeekPriceComparisonPercentage("loose"),
        text: this.getLastWeekPriceComparisonText("loose"),
      },
      palletLastWeekComparison: {
        percentage: this.getLastWeekPriceComparisonPercentage("pallet"),
        text: this.getLastWeekPriceComparisonText("pallet"),
      },
      looseNationalComparison: {
        percentage: this.getNationalPriceComparisonPercentage("loose"),
        price: this.getNationalPriceComparisonPrice("loose"),
        text: this.getNationalPriceComparisonText("loose"),
      },
      palletNationalComparison: {
        percentage: this.getNationalPriceComparisonPercentage("pallet"),
        price: this.getNationalPriceComparisonPrice("pallet"),
        text: this.getNationalPriceComparisonText("pallet"),
      },
    };
  }

  validateZipcode(): void {
    const isValidZipcode = this.cityRequestData.zipcode && this.zipcode;
    if (!isValidZipcode) {
      this.$nuxt.error({
        statusCode: 404,
      });
    }
  }

  getRandomCounties(counties): CountyLink[] {
    const filtered = counties.filter(
      (county) => county.name !== this.city.name,
    );
    const shuffled = [...filtered].sort(() => 0.5 - Math.random());
    return shuffled;
  }

  getActualLoosePrice(): number {
    return this.pelletsPriceHistory?.loose?.data?.length > 0
      ? this.pelletsPriceHistory.loose.data[
          this.pelletsPriceHistory.loose.data.length - 1
        ].y
      : 20;
  }

  getActualPalletPrice(): number {
    return this.pelletsPriceHistory?.pallet?.data?.length > 0
      ? this.pelletsPriceHistory.pallet.data[
          this.pelletsPriceHistory.pallet.data.length - 1
        ].y
      : 20;
  }

  getLastWeekPriceComparisonPercentage(type: "loose" | "pallet"): string {
    return this.getLastWeekPriceComparison(type, "percentage");
  }

  getLastWeekPriceComparisonText(type: "loose" | "pallet"): string {
    return this.getLastWeekPriceComparison(type, "text");
  }

  getNationalPriceComparison(
    type: "loose" | "pallet",
    returnType: "percentage" | "price" | "text",
  ): string | number {
    const nationalPrice =
      this.$store.state.oilprice.pelletsStateAssetsPrice?.[type]?.currentPrice
        ?.value || 0;
    const actualPrice = this.getActualPrice(type);

    if (returnType === "percentage") {
      return String(
        this.calcPercentageValue(nationalPrice, actualPrice),
      ).replace(".", ",");
    } else if (returnType === "price") {
      return Math.abs(actualPrice - nationalPrice);
    } else {
      return actualPrice <= nationalPrice ? "günstiger" : "teurer";
    }
  }

  getNationalPriceComparisonPercentage(type: "loose" | "pallet"): string {
    return this.getNationalPriceComparison(type, "percentage") as string;
  }

  getNationalPriceComparisonPrice(type: "loose" | "pallet"): number {
    return this.getNationalPriceComparison(type, "price") as number;
  }

  getNationalPriceComparisonText(type: "loose" | "pallet"): string {
    return this.getNationalPriceComparison(type, "text") as string;
  }

  getPriceAverageComparison(type: "loose" | "pallet"): string {
    const data = this.pelletsPriceHistory?.[type]?.data || [];
    if (data.length < 7) return "unter";

    const last7Days = data.slice(Math.max(data.length - 7, 0));
    const avgPrice =
      last7Days.reduce((sum, item) => sum + item.y, 0) / last7Days.length;
    const currentPrice = this.getActualPrice(type);

    return currentPrice <= avgPrice ? "unter" : "über";
  }

  getActualPrice(type: "loose" | "pallet"): number {
    return this.pelletsPriceHistory?.[type]?.data?.length > 0
      ? this.pelletsPriceHistory[type].data[
          this.pelletsPriceHistory[type].data.length - 1
        ].y
      : 20;
  }

  calcPercentageValue(a: number, b: number): number {
    if (a === 0 || b === 0) return 0;
    const result = Math.abs((a / b) * 100 - 100);
    return Math.round(result * 10) / 10;
  }

  get returnPriceFormatDE() {
    return returnPriceFormatDE;
  }

  get selectedOptionText(): string {
    switch (this.currentPelletstype) {
      case "loose":
        return "lose Ware";
      case "pallet":
        return "Sackware";
      case "bigbag":
        return "Bigbag";
      default:
        return "lose Ware";
    }
  }

  get actualPrice(): number {
    return this.dealerOffersData.dealerOffers[0]?.pricing?.ton?.brutto || 0;
  }

  get currentPelletstype() {
    return this.$route.query.prod ?? "loose";
  }

  get isNewUrlStructure(): boolean {
    return this.$route.params.id.startsWith("pelletspreise-");
  }

  get cityPlacesWithoutRequestedCity() {
    return this.cityPlaces.filter((elem) => elem.name !== this.city.name);
  }

  get locationFromUrl() {
    return this.$route.params.id.split("-").slice(1, -1).join("-");
  }

  get limitedCityPlaces(): CountyLink[] {
    return this.cityPlacesWithoutRequestedCity.slice(0, this.MAX_ITEMS_LIMIT);
  }

  get randomCountyLinks(): CountyLink[] {
    return this.getRandomCounties(
      this.$store.state.federalstates.communityLinks,
    );
  }

  get limitedRandomCountyLinks() {
    return this.randomCountyLinks
      .slice(0, this.MAX_ITEMS_LIMIT)
      .map((link) => ({
        name: link.name,
        link: {
          path: link.link,
          title: `Erfahren Sie mehr über die aktuelle Pelletspreis-Entwicklung in ${link.name}`,
        },
      }));
  }

  get nearbyZipcodeLinks() {
    if (!this._otherZipcodes?.length) {
      return [];
    }

    const location = this.$route.params.id.split("-").slice(1, -1).join("-");

    return [...this._otherZipcodes]
      .filter((zipcode) => zipcode !== this.zipcode)
      .sort(() => 0.5 - Math.random())
      .slice(0, 25)
      .map((zipcode) => ({
        name: `${zipcode} ${this.city.name}`,
        link: {
          path: `pelletspreise-${location}-${zipcode}`,
          title: `Erfahren Sie mehr über die aktuelle Pelletspreis-Entwicklung in ${zipcode} ${this.city.name}`,
        },
      }));
  }

  get showNearByLinks(): boolean {
    return (
      this._otherZipcodes.filter((zipcode) => zipcode !== this.zipcode).length >
      2
    );
  }

  get cityPlacesExceedsLimit(): boolean {
    return this.cityPlacesWithoutRequestedCity.length > this.MAX_ITEMS_LIMIT;
  }

  get randomCountyLinksExceedsLimit(): boolean {
    return this.randomCountyLinks.length > this.MAX_ITEMS_LIMIT;
  }

  get priceComparisonText(): string {
    const looseComparison = this.getPriceAverageComparison("loose");
    const palletComparison = this.getPriceAverageComparison("pallet");

    if (looseComparison === palletComparison) {
      return `Beide Preise liegen ${looseComparison} dem laufenden Durchschnitt der letzten 7 Tage.`;
    } else {
      return `Pellets lose Ware liegt ${looseComparison} dem laufenden Durchschnitt der letzten 7 Tage. Pellets Sackware liegt ${palletComparison} dem laufenden Durchschnitt der letzten 7 Tage.`;
    }
  }

  @Watch("city.name")
  async onCityChange() {
    this.$store.commit("federalstates/clearCountyLinks");
    await this.fetchCityPlaces(this.city.name);
    await this.handleCountyLinks();

    const zipcodeFromUrl = this.$route.params.id.split("-").pop();
    if (
      this.$route.query?.zipcode &&
      this.$route.query.zipcode !== zipcodeFromUrl
    ) {
      await this.updateRouteOnCityChange(this.$route.query.zipcode);
      this.storeInitialPelletData();
    }
  }

  beforeDestroy() {
    this.$store.commit("federalstates/clearCommunityLinks");
  }
}
